import { useState, useRef, useEffect, useCallback } from 'react';
import { useVoiceRecorder } from '../../hooks/useVoiceRecorder';
import { transcribeApi } from '../../api/transcribeApi';
import { chatApi, type AIUsageInfo, type ChatAction } from '../../api/chatApi';
import { useAuthStore } from '../../store/authStore';
import { useTabNavigationStore } from '../../store/tabNavigationStore';
import { useAIFilterStore } from '../../store/aiFilterStore';
import { PendingActionCard } from './PendingActionCard';

interface ChatMessage {
  id: string;
  role: 'user' | 'assistant';
  text: string;
  action?: ChatAction;
  status?: 'pending' | 'processing' | 'completed' | 'failed';
  created_at?: number; // milliseconds timestamp for ordering
}

function formatDuration(seconds: number): string {
  const m = Math.floor(seconds / 60);
  const s = seconds % 60;
  return `${m}:${s.toString().padStart(2, '0')}`;
}

export function AIAssistantSheet() {
  const isOpen = useTabNavigationStore((s) => s.isHubOpen);
  const closeHub = useTabNavigationStore((s) => s.closeHub);
  const switchTab = useTabNavigationStore((s) => s.switchTab);
  const setAIFilter = useAIFilterStore((s) => s.setFilter);
  const clearAIFilter = useAIFilterStore((s) => s.clearFilter);

  const [messages, setMessages] = useState<ChatMessage[]>([]);
  const [inputValue, setInputValue] = useState('');
  const [aiUsage, setAiUsage] = useState<AIUsageInfo | null>(null);
  const [lastLoadedAssistantId, setLastLoadedAssistantId] = useState<string | null>(null);
  const messagesEndRef = useRef<HTMLDivElement>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const currentAssistantId = useAuthStore((s) => s.currentAssistantId);

  // Long polling refs
  const abortControllerRef = useRef<AbortController | null>(null);
  const lastPollTimestampRef = useRef<number>(0);
  const isPollingRef = useRef<boolean>(false);
  const currentAssistantIdRef = useRef<string | null>(null);

  const {
    state: voiceState,
    duration: voiceDuration,
    startRecording,
    stopRecording,
    cancelRecording,
    setProcessing,
    reset: resetVoice,
    error: voiceError,
    permissionStatus, // Available for future UX improvements (e.g., show permission hint)
  } = useVoiceRecorder();

  // Log permission status for debugging
  console.log('🎤 Microphone permission status:', permissionStatus);

  // Keep assistantId ref in sync for polling loop (avoids stale closures)
  useEffect(() => {
    currentAssistantIdRef.current = currentAssistantId;
  }, [currentAssistantId]);

  // Track message count to only auto-scroll when NEW messages appear (not on poll updates)
  const prevMessageCountRef = useRef(0);
  useEffect(() => {
    if (messages.length > prevMessageCountRef.current) {
      messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
    }
    prevMessageCountRef.current = messages.length;
  }, [messages]);

  const handleAction = useCallback((action: ChatAction, closeAfter = true) => {
    if (action.type === 'navigate_athletes' && action.filter_ids && action.filter_ids.length > 0) {
      // Устанавливаем AI-фильтр
      setAIFilter(action.filter_ids, action.filter_label || 'AI фильтр');

      if (closeAfter) {
        // Небольшая задержка чтобы пользователь увидел ответ
        setTimeout(() => {
          // Закрываем Штаб
          closeHub();
          // Переключаемся на вкладку Athletes
          switchTab('athletes');
        }, 300);
      } else {
        // Просто переключаемся на вкладку без закрытия Штаба
        switchTab('athletes');
      }
    }
  }, [setAIFilter, closeHub, switchTab]);

  // Stop long polling
  const stopPolling = useCallback(() => {
    isPollingRef.current = false;
    abortControllerRef.current?.abort();
    abortControllerRef.current = null;
  }, []);

  // Continuous long polling — fetches ANY new messages from the server
  const startPolling = useCallback(async () => {
    if (isPollingRef.current) return;
    isPollingRef.current = true;
    let retryDelay = 3000;

    while (isPollingRef.current) {
      const assistantId = currentAssistantIdRef.current;
      if (!assistantId) {
        await new Promise((r) => setTimeout(r, 1000));
        continue;
      }

      const controller = new AbortController();
      abortControllerRef.current = controller;

      try {
        const newMessages = await chatApi.pollForNewMessages(
          assistantId,
          lastPollTimestampRef.current,
          controller.signal
        );
        if (!isPollingRef.current) break;

        if (newMessages.length > 0) {
          retryDelay = 3000; // reset backoff on success

          // Update lastTimestamp to max updated_at of returned messages (milliseconds)
          // Using updated_at ensures we don't miss messages that were created (reserved)
          // before the cursor but completed (updated) after it.
          const maxTs = Math.max(
            ...newMessages.map((m) => new Date(m.updated_at || m.created_at).getTime())
          );
          lastPollTimestampRef.current = maxTs;

          // Upsert messages into state (update existing by ID, append new)
          setMessages((prev) => {
            const map = new Map(prev.map((m) => [m.id, m]));
            let needUsageRefresh = false;

            for (const pollMsg of newMessages) {
              const existing = map.get(pollMsg.id);
              const pollCreatedAt = new Date(pollMsg.created_at).getTime();
              if (existing) {
                // Update existing message (e.g. pending → completed)
                if (existing.status === 'pending' && existing.role === 'assistant') {
                  needUsageRefresh = true;
                  if (pollMsg.action) {
                    handleAction(pollMsg.action, true);
                  }
                }
                map.set(pollMsg.id, {
                  ...existing,
                  text: pollMsg.content,
                  status: pollMsg.status as ChatMessage['status'],
                  action: pollMsg.action,
                  created_at: existing.created_at || pollCreatedAt,
                });
              } else {
                // New message (e.g. from another session or missed)
                map.set(pollMsg.id, {
                  id: pollMsg.id,
                  role: pollMsg.role,
                  text: pollMsg.content,
                  action: pollMsg.action,
                  status: pollMsg.status as ChatMessage['status'],
                  created_at: pollCreatedAt,
                });
              }
            }

            if (needUsageRefresh) {
              chatApi
                .getAIUsage()
                .then((u) => {
                  if (u && typeof u.used === 'number' && typeof u.limit === 'number') {
                    setAiUsage(u);
                  }
                })
                .catch((err) => console.error('Failed to update AI usage:', err));
            }

            // Sort by created_at to guarantee correct order
            // (prevents race condition between polling and sendMessage)
            return Array.from(map.values()).sort(
              (a, b) => (a.created_at || 0) - (b.created_at || 0)
            );
          });
        }
        // Server already held connection for up to 25 seconds — retry immediately
      } catch (err: any) {
        if (!isPollingRef.current) break;
        if (err?.name === 'AbortError' || err?.code === 'ERR_CANCELED') break;

        console.error('Long poll error:', err);
        await new Promise((r) => setTimeout(r, retryDelay));
        retryDelay = Math.min(retryDelay * 1.5, 15000);
      }
    }
  }, [handleAction]);

  // Загрузка истории и запуск long polling
  useEffect(() => {
    if (!isOpen || !currentAssistantId) {
      stopPolling();
      return;
    }
    if (lastLoadedAssistantId === currentAssistantId) return;

    // Остановить предыдущий polling перед загрузкой новых данных
    stopPolling();

    let cancelled = false;

    const loadData = async () => {
      try {
        const [history, usage] = await Promise.all([
          chatApi.getHistory(currentAssistantId, 50),
          chatApi.getAIUsage(),
        ]);

        if (cancelled) return;

        const chatMessages: ChatMessage[] = history.map((msg) => ({
          id: msg.id,
          role: msg.role,
          text: msg.content,
          action: msg.action,
          created_at: new Date(msg.created_at).getTime(),
        }));

        setMessages(chatMessages);
        if (usage && typeof usage.used === 'number' && typeof usage.limit === 'number') {
          setAiUsage(usage);
        }
        setLastLoadedAssistantId(currentAssistantId);

        // Set initial poll timestamp from last message in history (milliseconds)
        // Use updated_at as poll cursor — it reflects when message was completed
        if (history.length > 0) {
          const lastMsg = history[history.length - 1];
          lastPollTimestampRef.current = new Date(lastMsg.updated_at || lastMsg.created_at).getTime();
        } else {
          lastPollTimestampRef.current = Date.now();
        }

        // Start continuous long polling
        if (!cancelled) {
          startPolling();
        }
      } catch (err) {
        if (cancelled) return;
        console.error('Failed to load chat data:', err);
        setLastLoadedAssistantId(currentAssistantId);
      }
    };

    loadData();

    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen, currentAssistantId, lastLoadedAssistantId]);

  // Сбрасываем при смене ассистента
  useEffect(() => {
    return () => {
      stopPolling();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentAssistantId]);

  // Останавливаем polling при закрытии штаба
  useEffect(() => {
    if (!isOpen) {
      stopPolling();
      setMessages([]);
      setLastLoadedAssistantId(null);
      setAiUsage(null);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen]);

  const sendMessage = useCallback(
    async (text: string) => {
      if (!currentAssistantId) return;  // Убрали проверку isLoading

      // Сбрасываем AI-фильтр от предыдущего запроса
      clearAIFilter();

      try {
        // 1. Enqueue message (Long Polling) - возвращает оба ID
        const { user_message_id, assistant_message_id } = await chatApi.enqueueMessage(currentAssistantId, text);

        // 2. Добавляем ОБА сообщения в UI сразу
        const now = Date.now();
        setMessages((prev) => [
          ...prev,
          {
            id: user_message_id,
            role: 'user',
            text,
            status: 'completed',  // User message всегда completed сразу
            created_at: now,
          },
          {
            id: assistant_message_id,
            role: 'assistant',
            text: '',  // Пустой placeholder
            status: 'pending',
            created_at: now + 1, // assistant всегда ПОСЛЕ user
          },
        ]);

        // Polling loop автоматически подхватит completed assistant message
      } catch (err: any) {
        // Бэкенд возвращает типизированные ошибки:
        // { code: "ai_limit_exceeded" | "rate_limit" | "context_too_long" | "llm_overload" | "internal_error",
        //   user_message: "..." }
        const errorCode = err?.code || '';
        const userMessage = err?.user_message || '';

        let errorText: string;

        if (errorCode === 'ai_limit_exceeded' || err?.error === 'AI request limit exceeded') {
          errorText = userMessage || 'Лимит AI-запросов исчерпан. Обновите тариф для продолжения.';
          // Обновить AI usage если есть данные
          if (err?.ai_usage) {
            setAiUsage((prev) => prev ? {
              ...prev,
              used: err.ai_usage.used,
              remaining: 0,
            } : null);
          }
        } else if (userMessage) {
          // Используем user-friendly сообщение от бэкенда
          errorText = userMessage;
        } else if (err?.error === 'Network error') {
          errorText = 'Нет связи с сервером. Проверьте подключение к интернету.';
        } else {
          errorText = 'Произошла ошибка. Попробуйте ещё раз.';
        }

        setMessages((prev) => [
          ...prev,
          {
            id: `error-${Date.now()}`,
            role: 'assistant',
            text: errorText,
          },
        ]);
        console.error('Chat error:', { code: errorCode, userMessage, raw: err });
      }
    },
    [currentAssistantId, clearAIFilter]
  );

  const handleSend = () => {
    const text = inputValue.trim();
    if (!text) return;
    sendMessage(text);
    setInputValue('');
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSend();
    }
  };

  // --- File attachment handler ---
  const handleFileAttach = () => {
    fileInputRef.current?.click();
  };

  const handleFileChange = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;

    // Reset input so same file can be selected again
    e.target.value = '';

    // Size limit: 1MB
    if (file.size > 1 * 1024 * 1024) {
      setMessages((prev) => [
        ...prev,
        {
          id: `file-error-${Date.now()}`,
          role: 'assistant',
          text: '❌ Файл слишком большой (максимум 1 МБ). Отправьте файл поменьше или вставьте текст в сообщение.',
        },
      ]);
      return;
    }

    // Allowed text file extensions
    const allowedExtensions = [
      '.txt', '.csv', '.md', '.json', '.xml', '.html', '.htm',
      '.log', '.yaml', '.yml', '.ini', '.cfg', '.conf', '.toml',
    ];
    const fileName = file.name.toLowerCase();
    const isAllowed = allowedExtensions.some((ext) => fileName.endsWith(ext)) ||
      file.type.startsWith('text/') ||
      file.type === 'application/json' ||
      file.type === 'application/xml';

    if (!isAllowed) {
      setMessages((prev) => [
        ...prev,
        {
          id: `file-error-${Date.now()}`,
          role: 'assistant',
          text: '❌ Формат файла не поддерживается. Поддерживаются текстовые файлы: .txt, .csv, .md, .json, .xml, .yaml, .html, .log\n\nДля других форматов скопируйте текст и отправьте его сообщением.',
        },
      ]);
      return;
    }

    try {
      const text = await file.text();
      const trimmed = text.trim();
      if (!trimmed) {
        setMessages((prev) => [
          ...prev,
          {
            id: `file-error-${Date.now()}`,
            role: 'assistant',
            text: '❌ Файл пуст или не содержит текста.',
          },
        ]);
        return;
      }

      // Limit text length
      const maxLen = 15000;
      const fileText = trimmed.length > maxLen
        ? trimmed.slice(0, maxLen) + '\n\n... (текст обрезан, файл слишком длинный)'
        : trimmed;

      const message = `Обработай содержимое файла\n\nФайл: ${file.name}\n\n\`\`\`\n${fileText}\n\`\`\``;
      sendMessage(message);
    } catch {
      setMessages((prev) => [
        ...prev,
        {
          id: `file-error-${Date.now()}`,
          role: 'assistant',
          text: '❌ Не удалось прочитать файл. Попробуйте другой файл.',
        },
      ]);
    }
  };

  // --- Voice recording handlers ---

  const handleMicClick = async () => {
    await startRecording();
  };

  const handleVoiceSend = async () => {
    if (!currentAssistantId) {
      resetVoice();
      return;
    }

    const blob = await stopRecording();
    if (!blob) {
      resetVoice();
      return;
    }

    setProcessing();

    try {
      const result = await transcribeApi.transcribe(currentAssistantId, blob);
      if (result.text.trim()) {
        sendMessage(result.text.trim());
      } else {
        // STT вернул пустой текст — сообщаем пользователю
        setMessages((prev) => [
          ...prev,
          {
            id: `stt-empty-${Date.now()}`,
            role: 'assistant',
            text: 'Не удалось распознать сообщение. Попробуйте ещё раз или напишите текстом.',
          },
        ]);
      }
    } catch (err) {
      console.error('Transcription failed:', err);
      setMessages((prev) => [
        ...prev,
        {
          id: Date.now().toString(),
          role: 'assistant',
          text: 'Не удалось распознать голос. Попробуйте ещё раз.',
        },
      ]);
    } finally {
      resetVoice();
    }
  };

  const handleVoiceCancel = () => {
    cancelRecording();
  };

  // Waveform bars for recording animation
  const waveformHeights = useRef(
    Array.from({ length: 24 }, () => Math.random() * 24 + 8)
  );

  // AI limit exceeded?
  const isLimitExceeded = aiUsage && aiUsage.limit !== -1 && aiUsage.remaining <= 0;

  return (
    <div className="flex flex-col h-full pt-4">
      {/* AI Usage Badge */}
      {aiUsage && (
        <div className="flex items-center justify-end px-1 pb-2 flex-shrink-0">
          <div
            className="flex items-center gap-1.5 px-3 py-1 rounded-full text-[13px] leading-4"
            style={{
              backgroundColor: isLimitExceeded ? 'var(--danger-bg, rgba(255,59,48,0.1))' : 'var(--bg-secondary)',
              color: isLimitExceeded ? 'var(--danger)' : 'var(--text-secondary)',
            }}
          >
            <span>🤖</span>
            <span>
              {aiUsage.limit === -1
                ? `${aiUsage.used} запросов`
                : `${aiUsage.used}/${aiUsage.limit}`
              }
            </span>
          </div>
        </div>
      )}

      {/* Chat Messages */}
      <div className="flex-1 overflow-y-auto space-y-3 py-4 min-h-0">
        {messages.length === 0 && (
          <div className="flex justify-start">
            <div
              className="max-w-[80%] px-4 py-3 rounded-2xl rounded-bl-[4px]"
              style={{ backgroundColor: 'var(--bg-secondary)' }}
            >
              <p
                className="text-[17px] leading-[22px]"
                style={{ color: 'var(--text-primary)' }}
              >
                Здравствуйте! Я ваш AI-ассистент. Чем могу помочь?
              </p>
            </div>
          </div>
        )}
        {messages
          .filter((msg) => {
            // Скрываем пустые assistant messages (кроме pending — те показываем как "Думаю...")
            if (msg.role === 'assistant' && !msg.text && msg.status !== 'pending') return false;
            return true;
          })
          .map((msg) => (
          <div
            key={msg.id}
            className={`flex ${msg.role === 'user' ? 'justify-end' : 'justify-start'}`}
          >
            <div
              className={`max-w-[80%] px-4 py-3 ${
                msg.role === 'user'
                  ? 'rounded-2xl rounded-br-[4px]'
                  : 'rounded-2xl rounded-bl-[4px]'
              }`}
              style={{
                backgroundColor:
                  msg.role === 'user'
                    ? 'var(--accent-solid, var(--accent))'
                    : 'var(--bg-secondary)',
              }}
            >
              <p
                className="text-[17px] leading-[22px] whitespace-pre-wrap"
                style={{
                  color: msg.role === 'user' ? '#FFFFFF' : 'var(--text-primary)',
                }}
              >
                {msg.text}
              </p>
              {/* Индикатор обработки для pending сообщений */}
              {msg.status === 'pending' && (
                <div className="mt-2 flex items-center gap-2 text-xs opacity-70">
                  <svg
                    className="animate-spin h-3 w-3"
                    xmlns="http://www.w3.org/2000/svg"
                    fill="none"
                    viewBox="0 0 24 24"
                  >
                    <circle
                      className="opacity-25"
                      cx="12"
                      cy="12"
                      r="10"
                      stroke="currentColor"
                      strokeWidth="4"
                    />
                    <path
                      className="opacity-75"
                      fill="currentColor"
                      d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                    />
                  </svg>
                  <span style={{ color: msg.role === 'user' ? '#FFFFFF' : 'var(--text-secondary)' }}>
                    {msg.role === 'assistant' ? 'Думаю...' : 'Обработка...'}
                  </span>
                </div>
              )}
              {/* Кнопка действия если есть */}
              {(() => {
                const shouldShowButton = msg.role === 'assistant' && msg.action && msg.action.type === 'navigate_athletes';
                console.log('🔘 [Button] msg.id:', msg.id, 'role:', msg.role, 'hasAction:', !!msg.action, 'actionType:', msg.action?.type, 'shouldShow:', shouldShowButton);
                return shouldShowButton && msg.action ? (
                  <button
                    onClick={() => handleAction(msg.action!)}
                    className="mt-3 px-4 py-2 rounded-lg font-medium text-sm active:opacity-70 transition-opacity"
                    style={{
                      backgroundColor: 'var(--accent)',
                      color: '#FFFFFF',
                    }}
                  >
                    📋 Показать список ({msg.action?.filter_ids?.length || 0})
                  </button>
                ) : null;
              })()}
              {/* Массовые изменения ждут подтверждения: точный список и кнопки */}
              {msg.role === 'assistant' &&
                msg.status !== 'pending' &&
                msg.action?.pending_action_ids?.map((actionId) => (
                  <PendingActionCard key={actionId} id={actionId} />
                ))}
            </div>
          </div>
        ))}
        <div ref={messagesEndRef} />
      </div>

      {/* Input Area */}
      <div
        className="pb-4 pt-2 border-t-[0.5px] flex-shrink-0"
        style={{ borderColor: 'var(--separator)' }}
      >
        {/* Limit exceeded message */}
        {isLimitExceeded && (
          <p
            className="text-[13px] leading-4 text-center mb-2 px-4"
            style={{ color: 'var(--danger)' }}
          >
            Лимит AI-запросов исчерпан. Обновите тариф для продолжения.
          </p>
        )}

        {/* Voice error message */}
        {voiceError && (
          <p
            className="text-[13px] leading-4 text-center mb-2 px-4"
            style={{ color: 'var(--danger)' }}
          >
            {voiceError}
          </p>
        )}

        {voiceState === 'recording' ? (
          /* ===== RECORDING MODE ===== */
          <div className="flex flex-col gap-3">
            {/* Waveform + timer */}
            <div
              className="h-12 rounded-xl flex items-center gap-3 px-4"
              style={{ backgroundColor: 'var(--bg-secondary)' }}
            >
              {/* Recording indicator */}
              <div
                className="w-2.5 h-2.5 rounded-full flex-shrink-0 animate-pulse"
                style={{ backgroundColor: 'var(--danger)' }}
              />

              {/* Waveform */}
              <div className="flex-1 flex items-center gap-[3px]">
                {waveformHeights.current.map((h, i) => (
                  <div
                    key={i}
                    className="w-[3px] rounded-full"
                    style={{
                      height: `${h}px`,
                      backgroundColor: 'var(--accent)',
                      opacity: 0.6 + Math.random() * 0.4,
                      animation: `waveform-pulse 1.2s ease-in-out ${i * 60}ms infinite alternate`,
                    }}
                  />
                ))}
              </div>

              {/* Timer */}
              <span
                className="text-[15px] leading-5 tabular-nums flex-shrink-0"
                style={{ color: 'var(--text-primary)' }}
              >
                {formatDuration(voiceDuration)}
              </span>
            </div>

            {/* Cancel / Send buttons */}
            <div className="flex gap-3">
              <button
                onClick={handleVoiceCancel}
                className="flex-1 h-11 rounded-xl flex items-center justify-center gap-2"
                style={{ backgroundColor: 'var(--bg-secondary)' }}
              >
                {/* X icon */}
                <svg
                  width="18"
                  height="18"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="var(--text-hint)"
                  strokeWidth="2"
                  strokeLinecap="round"
                >
                  <path d="M18 6L6 18M6 6l12 12" />
                </svg>
                <span
                  className="text-[15px] leading-5"
                  style={{ color: 'var(--text-hint)' }}
                >
                  Отмена
                </span>
              </button>

              <button
                onClick={handleVoiceSend}
                className="flex-1 h-11 rounded-xl flex items-center justify-center gap-2"
                style={{ backgroundColor: 'var(--accent-solid, var(--accent))' }}
              >
                {/* Send icon */}
                <svg
                  width="18"
                  height="18"
                  viewBox="0 0 24 24"
                  fill="#FFFFFF"
                  stroke="none"
                >
                  <path d="M2.01 21L23 12 2.01 3 2 10l15 2-15 2z" />
                </svg>
                <span className="text-[15px] leading-5 text-white">
                  Отправить
                </span>
              </button>
            </div>
          </div>
        ) : voiceState === 'processing' ? (
          /* ===== PROCESSING MODE ===== */
          <div
            className="h-12 rounded-xl flex items-center justify-center gap-3 px-4"
            style={{ backgroundColor: 'var(--bg-secondary)' }}
          >
            {/* Spinner */}
            <svg
              className="animate-spin"
              width="20"
              height="20"
              viewBox="0 0 24 24"
              fill="none"
            >
              <circle
                cx="12"
                cy="12"
                r="10"
                stroke="var(--text-hint)"
                strokeWidth="3"
                strokeDasharray="50"
                strokeLinecap="round"
              />
            </svg>
            <span
              className="text-[15px] leading-5"
              style={{ color: 'var(--text-hint)' }}
            >
              Распознавание...
            </span>
          </div>
        ) : (
          /* ===== NORMAL INPUT MODE ===== */
          <div
            className={`h-12 rounded-xl flex items-center gap-3 px-4 ${isLimitExceeded ? 'opacity-50' : ''}`}
            style={{ backgroundColor: 'var(--bg-secondary)' }}
          >
            {/* Paperclip — file attachment */}
            <input
              ref={fileInputRef}
              type="file"
              className="hidden"
              accept=".txt,.csv,.md,.json,.xml,.html,.htm,.log,.yaml,.yml,.ini,.cfg,.conf,.toml,text/*,application/json,application/xml"
              onChange={handleFileChange}
            />
            <button className="flex-shrink-0" disabled={!!isLimitExceeded} onClick={handleFileAttach}>
              <svg
                width="20"
                height="20"
                viewBox="0 0 24 24"
                fill="none"
                stroke="var(--text-hint)"
                strokeWidth="2"
                strokeLinecap="round"
                strokeLinejoin="round"
              >
                <path d="M21.44 11.05l-9.19 9.19a6 6 0 01-8.49-8.49l9.19-9.19a4 4 0 015.66 5.66l-9.2 9.19a2 2 0 01-2.83-2.83l8.49-8.48" />
              </svg>
            </button>

            {/* Text input */}
            <input
              type="text"
              value={inputValue}
              onChange={(e) => setInputValue(e.target.value)}
              onKeyDown={handleKeyDown}
              placeholder={isLimitExceeded ? 'Лимит исчерпан' : 'Сообщение...'}
              disabled={!!isLimitExceeded}
              className="flex-1 bg-transparent border-none outline-none text-[17px] leading-[22px]"
              style={{ color: 'var(--text-primary)' }}
            />

            {/* Mic / Send button */}
            {inputValue.trim() ? (
              <button className="flex-shrink-0" onClick={handleSend} disabled={!!isLimitExceeded}>
                <svg
                  width="20"
                  height="20"
                  viewBox="0 0 24 24"
                  fill="var(--accent)"
                  stroke="none"
                >
                  <path d="M2.01 21L23 12 2.01 3 2 10l15 2-15 2z" />
                </svg>
              </button>
            ) : (
              <button className="flex-shrink-0" onClick={handleMicClick} disabled={!!isLimitExceeded}>
                <svg
                  width="20"
                  height="20"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="var(--accent)"
                  strokeWidth="2"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                >
                  <path d="M12 1a3 3 0 00-3 3v8a3 3 0 006 0V4a3 3 0 00-3-3z" />
                  <path d="M19 10v2a7 7 0 01-14 0v-2" />
                  <line x1="12" y1="19" x2="12" y2="23" />
                  <line x1="8" y1="23" x2="16" y2="23" />
                </svg>
              </button>
            )}
          </div>
        )}
      </div>

      {/* Placeholder input style + waveform keyframes */}
      <style>{`
        input::placeholder {
          color: var(--text-hint);
        }
        @keyframes waveform-pulse {
          0% { transform: scaleY(0.6); }
          100% { transform: scaleY(1.4); }
        }
      `}</style>
    </div>
  );
}
